import { useState } from "react";

import { Button } from "./ui/button";

// Decision 7 (Jason, 22 Oct 2026, docs/cleo-tool-system.md): "Coding agents (Claude Code and Codex) may open requests in
// my World and read my answers, but never decide." The Inbox card for one such question (approval HG-AGENT-ASK-01): which
// agent asks, the question in its words, the choices it offered, and a note he may add. He answers by picking a choice (or
// Yes when none were offered) or declines; the World keeps his choice and note for the agent to read (lib/agentAsk.js).

export const AGENT_QUESTION_NOTE_MAX = 1000;

export interface AgentQuestion {
  readonly question: string;
  readonly choices: readonly string[];
  readonly agentLabel: string;
  readonly context: { readonly title?: string; readonly url?: string } | null;
  /** What he answered, once decided (the World puts it on the card): his choice and his note. */
  readonly answer: { readonly choice?: string; readonly note?: string } | null;
}

export interface AgentQuestionAnswer {
  readonly outcome: "approved" | "declined";
  readonly answer?: { readonly choice?: string; readonly note?: string };
}

/** The question a row's card facts carry, or null when the row is not a coding agent's question. Pure. */
export function agentQuestionOf(cardFacts: unknown): AgentQuestion | null {
  if (typeof cardFacts !== "object" || cardFacts === null) return null;
  const facts = cardFacts as Record<string, unknown>;
  if (facts["kind"] !== "agent-question" || typeof facts["question"] !== "string") return null;
  const choices = Array.isArray(facts["choices"])
    ? facts["choices"].filter((choice): choice is string => typeof choice === "string")
    : [];
  const agent = facts["agent"] as Record<string, unknown> | undefined;
  const label =
    typeof agent?.["label"] === "string" && agent["label"] ? agent["label"] : "A coding agent";
  const raw = facts["context"] as Record<string, unknown> | undefined;
  const title = typeof raw?.["title"] === "string" ? raw["title"] : undefined;
  const url =
    typeof raw?.["url"] === "string" && /^https:\/\//.test(raw["url"]) ? raw["url"] : undefined;
  const given = facts["answer"] as Record<string, unknown> | undefined;
  const choice = typeof given?.["choice"] === "string" ? given["choice"] : undefined;
  const note = typeof given?.["note"] === "string" ? given["note"] : undefined;
  return {
    question: facts["question"],
    choices,
    agentLabel: label,
    context: title || url ? { ...(title ? { title } : {}), ...(url ? { url } : {}) } : null,
    answer: choice || note ? { ...(choice ? { choice } : {}), ...(note ? { note } : {}) } : null,
  };
}

/** What a press sends: his choice (none when no choices were offered) and the note, trimmed; declining picks none. Pure. */
export function agentQuestionAnswer(
  outcome: "approved" | "declined",
  choice: string | null,
  note: string,
): AgentQuestionAnswer {
  const said = note.trim();
  const answer = {
    ...(outcome === "approved" && choice !== null ? { choice } : {}),
    ...(said ? { note: said } : {}),
  };
  return Object.keys(answer).length ? { outcome, answer } : { outcome };
}

export function AgentQuestionCard({
  question,
  awaiting,
  pending,
  error,
  onAnswer,
}: {
  readonly question: AgentQuestion;
  readonly awaiting: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onAnswer: (answer: AgentQuestionAnswer) => void;
}) {
  const [note, setNote] = useState("");
  const send = (outcome: "approved" | "declined", choice: string | null) =>
    onAnswer(agentQuestionAnswer(outcome, choice, note));
  return (
    <div className="rounded-lg border border-border/40 p-3" data-cleo="inbox-agent-question">
      <p className="text-xs font-medium text-foreground">{question.agentLabel} asks you</p>
      <p
        className="mt-2 whitespace-pre-wrap text-sm text-foreground"
        data-cleo="inbox-agent-question-text"
      >
        {question.question}
      </p>
      {question.context ? (
        <p className="mt-1 truncate text-[10.5px] text-muted-foreground">
          {question.context.url ? (
            <a href={question.context.url} target="_blank" rel="noreferrer" className="underline">
              {question.context.title ?? question.context.url}
            </a>
          ) : (
            question.context.title
          )}
        </p>
      ) : null}
      {!awaiting && question.answer ? (
        <dl
          className="mt-2 grid grid-cols-[72px_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs"
          data-cleo="inbox-agent-question-answer"
        >
          {question.answer.choice ? (
            <>
              <dt className="text-muted-foreground">You chose</dt>
              <dd className="text-foreground">{question.answer.choice}</dd>
            </>
          ) : null}
          {question.answer.note ? (
            <>
              <dt className="text-muted-foreground">Your note</dt>
              <dd className="whitespace-pre-wrap text-foreground">{question.answer.note}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
      {awaiting ? (
        <>
          <textarea
            className="mt-3 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
            placeholder="Add a note for the agent (optional)"
            aria-label="A note for the agent"
            maxLength={AGENT_QUESTION_NOTE_MAX}
            rows={2}
            value={note}
            disabled={pending}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {question.choices.length > 0 ? (
              question.choices.map((choice) => (
                <Button
                  key={choice}
                  size="sm"
                  disabled={pending}
                  data-cleo="inbox-agent-question-choice"
                  onClick={() => send("approved", choice)}
                >
                  {choice}
                </Button>
              ))
            ) : (
              <Button size="sm" disabled={pending} onClick={() => send("approved", null)}>
                Yes
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              disabled={pending}
              data-cleo="inbox-agent-question-decline"
              onClick={() => send("declined", null)}
            >
              {question.choices.length > 0 ? "Decline" : "No"}
            </Button>
          </div>
          <p className="mt-2 text-[10.5px] text-muted-foreground">
            Only you answer this. The agent reads your answer; it decides nothing.
          </p>
        </>
      ) : null}
      {error ? (
        <p className="mt-2 text-xs text-warning" role="status">
          {error}
        </p>
      ) : null}
    </div>
  );
}
